import { useState, useCallback, useRef, useEffect } from 'react'

import { useLazyFetchConfiguredServiceDetailsQuery } from 'api/configured-service/configuredServiceCrud'
import { useLazyFetchWorkOrderListQuery } from 'api/work-order/fetchWorkOrderList'
import { DEFAULT_SORT_RULES_BY_SERVICE_DATE_DESC } from 'components/customer-details/content/work-orders/settings'
import { formatDateToBEFormat } from 'utils/date'

const OPEN_DELAY_MS = 700
const CLOSE_DELAY_MS = 1000

/**
 * Hook to manage a single shared popover/dialog instance for multiple markers on a map.
 * This significantly reduces the number of hooks and state instances when rendering thousands of markers.
 */
export const useSharedMarkerPopover = ({ startDate, endDate }) => {
  const [activeMarker, setActiveMarker] = useState(null)
  const [infoWindowShown, setInfoWindowShown] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [serviceDetails, setServiceDetails] = useState(null)
  const [weekWorkOrders, setWeekWorkOrders] = useState([])

  const [fetchWorkOrderList, workOrdersRequest] = useLazyFetchWorkOrderListQuery()
  const [fetchCSDetails, csDetailsRequest] = useLazyFetchConfiguredServiceDetailsQuery()

  const hoverRef = useRef(false)
  const popoverHoverRef = useRef(false)
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearTimers()
      hoverRef.current = false
      popoverHoverRef.current = false
    }
  }, [clearTimers])

  const handleMarkerMouseEnter = useCallback(
    (e, anchor, details) => {
      hoverRef.current = true
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }

      // If already showing this marker, don't restart timer
      if (activeMarker?.details?.id === details.id && (infoWindowShown || dialogOpen)) return

      // deck.gl passes a {lat, lng} LatLng as first arg instead of a DOM event
      const isLatLng = e && typeof e === 'object' && 'lat' in e
      const latLng = isLatLng ? e : null
      const resolvedAnchor = isLatLng ? null : anchor

      if (openTimerRef.current) clearTimeout(openTimerRef.current)
      openTimerRef.current = setTimeout(() => {
        if (!hoverRef.current) return
        setActiveMarker({ anchor: resolvedAnchor, latLng, details })
        setInfoWindowShown(true)
      }, OPEN_DELAY_MS)
    },
    [activeMarker, infoWindowShown, dialogOpen]
  )

  const handleMarkerMouseLeave = useCallback(() => {
    hoverRef.current = false
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }

    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      if (!hoverRef.current && !popoverHoverRef.current) {
        setInfoWindowShown(false)
      }
    }, CLOSE_DELAY_MS)
  }, [])

  const handlePopoverMouseEnter = useCallback(() => {
    popoverHoverRef.current = true
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const handlePopoverMouseLeave = useCallback(() => {
    popoverHoverRef.current = false
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(() => {
      if (!hoverRef.current && !popoverHoverRef.current) {
        setInfoWindowShown(false)
      }
    }, CLOSE_DELAY_MS)
  }, [])

  const handleMarkerClick = useCallback((e, anchor, details) => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    // deck.gl passes a {lat, lng} LatLng as first arg instead of a DOM event
    const isLatLng = e && typeof e === 'object' && 'lat' in e
    const latLng = isLatLng ? e : null
    const resolvedAnchor = isLatLng ? null : anchor
    setInfoWindowShown(false)
    setActiveMarker({ anchor: resolvedAnchor, latLng, details })
    setDialogOpen(true)
  }, [])

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false)
  }, [])

  // Fetch data when popover or dialog opens
  useEffect(() => {
    const id = activeMarker?.details?.id
    if ((infoWindowShown || dialogOpen) && id) {
      fetchCSDetails({ id })
        .unwrap()
        .then(res => setServiceDetails(res || null))
        .catch(() => setServiceDetails(null))

      if (startDate && endDate) {
        fetchWorkOrderList({
          configuredServiceIds: [id],
          startDate: formatDateToBEFormat(startDate),
          endDate: formatDateToBEFormat(endDate),
          requestedPage: 0,
          requestedPageSize: 50,
          sortRules: [...DEFAULT_SORT_RULES_BY_SERVICE_DATE_DESC],
        })
          .unwrap()
          .then(res => setWeekWorkOrders(res?.workOrders || []))
          .catch(() => setWeekWorkOrders([]))
      }
    } else if (!infoWindowShown && !dialogOpen) {
      // Clear data when closed
      setServiceDetails(null)
      setWeekWorkOrders([])
    }
  }, [infoWindowShown, dialogOpen, activeMarker?.details?.id, startDate, endDate, fetchCSDetails, fetchWorkOrderList])

  const isLoadingPopover = csDetailsRequest?.isLoading || workOrdersRequest?.isLoading

  return {
    activeMarker,
    infoWindowShown,
    dialogOpen,
    serviceDetails,
    weekWorkOrders,
    isLoadingPopover,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handleMarkerClick,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    handleDialogClose,
  }
}
